import { useEffect, useState, useRef } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Mail, Send, Search, Plus, Inbox, Reply, ChevronRight, Copy, CheckCircle2 } from "lucide-react";

const MessagesPage = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("inbox");
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ to_user_id: "", to_name: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const threadEnd = useRef();

  const loadInbox = async () => {
    const r = await api.get("/messages/inbox");
    setInbox(r.data.messages || []);
  };
  const loadSent = async () => {
    const r = await api.get("/messages/sent");
    setSent(r.data.messages || []);
  };
  const loadContacts = async (q = "") => {
    const r = await api.get(`/messages/contacts${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setContacts(r.data.contacts || []);
  };

  useEffect(() => { loadInbox(); loadSent(); loadContacts(); }, []);

  useEffect(() => { if (threadEnd.current) threadEnd.current.scrollIntoView({ behavior: "smooth" }); }, [threadMsgs]);

  const openThread = async (otherUserId, otherName) => {
    setActiveThread({ id: otherUserId, name: otherName });
    const r = await api.get(`/messages/thread/${otherUserId}`);
    setThreadMsgs(r.data.messages || []);
    loadInbox(); // refresh unread state
  };

  const sendMessage = async () => {
    if (!form.to_user_id) { toast.error("Pick a recipient"); return; }
    if (!form.subject || !form.body) { toast.error("Subject + body required"); return; }
    setSending(true);
    try {
      await api.post("/messages/send", {
        to_user_id: form.to_user_id,
        subject: form.subject,
        body: form.body,
      });
      toast.success("Message sent");
      setComposeOpen(false);
      setForm({ to_user_id: "", to_name: "", subject: "", body: "" });
      loadSent();
      // If we have a thread open with that user, refresh it
      if (activeThread?.id === form.to_user_id) openThread(form.to_user_id, form.to_name);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const reply = (msg) => {
    setForm({
      to_user_id: msg.from_user_id,
      to_name: msg.from_name,
      subject: msg.subject.startsWith("Re:") ? msg.subject : `Re: ${msg.subject}`,
      body: "",
    });
    setComposeOpen(true);
  };

  const filteredContacts = search
    ? contacts.filter((c) =>
        (c.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (c.internal_email || "").toLowerCase().includes(search.toLowerCase())
      )
    : contacts;

  const [copiedAddr, setCopiedAddr] = useState(false);
  const copyAddress = async () => {
    if (!user?.internal_email) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(user.internal_email);
      } else {
        const ta = document.createElement("textarea");
        ta.value = user.internal_email;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) { console.debug("MessagesPage: execCommand copy failed", e); }
        document.body.removeChild(ta);
      }
      setCopiedAddr(true);
      toast.success("Portal address copied");
      setTimeout(() => setCopiedAddr(false), 1500);
    } catch (err) {
      toast.error("Could not copy — please copy manually");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="messages-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Mail className="text-blue-600" size={16} />
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Internal portal messaging
              </span>
            </div>
            <h1 className="text-3xl font-serif tracking-tight text-slate-900">Messages</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-3xl">
              Direct, secure communication between EchoSense Capital Club members. Messages are private to
              your portal account — no email forwarding, no third-party servers.
            </p>
          </div>
          <Button onClick={() => setComposeOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="compose-btn">
            <Plus size={14} className="mr-1" /> Compose
          </Button>
        </div>

        {/* Portal address card — shows the user's f.last@echosensecapital.com handle */}
        {user?.internal_email && (
          <Card className="bg-gradient-to-r from-blue-50 to-white border-blue-100" data-testid="portal-address-card">
            <CardContent className="p-4 flex items-center gap-4 flex-wrap">
              <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white shrink-0">
                <Mail size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-blue-700/80">Your portal address</p>
                <p className="text-base font-mono text-slate-900 mt-0.5 truncate" data-testid="portal-address-value">{user.internal_email}</p>
                <p className="text-xs text-slate-500 mt-1">Use this address to receive internal portal messages from EchoSense Capital Club members.</p>
              </div>
              <Button onClick={copyAddress} variant="outline" size="sm" className="border-blue-200 text-blue-700 hover:bg-blue-50 shrink-0" data-testid="copy-portal-address">
                {copiedAddr ? <CheckCircle2 size={14} className="mr-1 text-emerald-500" /> : <Copy size={14} className="mr-1" />}
                {copiedAddr ? "Copied" : "Copy"}
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left: list of conversations */}
          <Card className="bg-white border-slate-200 md:col-span-1">
            <CardContent className="p-0">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="w-full grid grid-cols-2 rounded-none border-b border-slate-100">
                  <TabsTrigger value="inbox" className="rounded-none" data-testid="tab-inbox">
                    <Inbox size={14} className="mr-1" /> Inbox
                    {inbox.filter((m) => !m.read_at).length > 0 && (
                      <Badge className="ml-2 bg-blue-600 text-white border-0 h-4 text-[10px]" data-testid="inbox-unread-badge">
                        {inbox.filter((m) => !m.read_at).length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="sent" className="rounded-none" data-testid="tab-sent">
                    <Send size={14} className="mr-1" /> Sent
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="inbox" className="m-0">
                  <MessageList items={inbox} side="from" onClick={(m) => openThread(m.from_user_id, m.from_name)} />
                </TabsContent>
                <TabsContent value="sent" className="m-0">
                  <MessageList items={sent} side="to" onClick={(m) => openThread(m.to_user_id, m.to_name)} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Right: thread viewer */}
          <Card className="bg-white border-slate-200 md:col-span-2 min-h-[400px]" data-testid="thread-viewer">
            <CardContent className="p-0 flex flex-col h-full">
              {!activeThread ? (
                <div className="flex-1 flex items-center justify-center p-12 text-slate-400 text-sm">
                  Select a conversation on the left, or compose a new message.
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-100 p-4 flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-slate-900">{activeThread.name}</h3>
                      <p className="text-xs text-slate-500">Direct conversation</p>
                    </div>
                    <Button
                      onClick={() => {
                        setForm({ to_user_id: activeThread.id, to_name: activeThread.name, subject: "", body: "" });
                        setComposeOpen(true);
                      }}
                      variant="outline" size="sm" data-testid="thread-reply-btn"
                    >
                      <Reply size={12} className="mr-1" /> New message
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[60vh]" data-testid="thread-messages">
                    {threadMsgs.map((m) => (
                      <div key={m.id} className="border-b border-slate-100 pb-3 last:border-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-slate-900">{m.from_name}</p>
                          <p className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</p>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">{m.from_internal_email}</p>
                        <p className="text-sm font-medium text-slate-700 mt-1">{m.subject}</p>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">{m.body}</p>
                      </div>
                    ))}
                    <div ref={threadEnd} />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Compose dialog */}
        <Dialog open={composeOpen} onOpenChange={(o) => !o && setComposeOpen(false)}>
          <DialogContent className="bg-white max-w-lg" data-testid="compose-dialog">
            <DialogHeader>
              <DialogTitle>New message</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>To</Label>
                {form.to_name ? (
                  <div className="p-2 rounded border border-slate-200 bg-slate-50 flex items-center justify-between">
                    <span className="text-sm">{form.to_name}</span>
                    <button onClick={() => setForm({ ...form, to_user_id: "", to_name: "" })} className="text-xs text-slate-500 hover:text-red-600">change</button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="pl-9" data-testid="contact-search" />
                    </div>
                    <div className="mt-2 max-h-40 overflow-y-auto border border-slate-100 rounded">
                      {filteredContacts.slice(0, 30).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => setForm({ ...form, to_user_id: c.id, to_name: c.full_name })}
                          className="w-full text-left p-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex items-center justify-between"
                          data-testid={`contact-${c.id}`}
                        >
                          <div>
                            <p className="text-sm font-medium text-slate-900">{c.full_name}</p>
                            <p className="text-xs text-slate-500">{c.internal_email || c.email} · {c.role}</p>
                          </div>
                          <ChevronRight size={14} className="text-slate-400" />
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div>
                <Label>Subject</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="compose-subject" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="compose-body" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button onClick={sendMessage} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm" data-testid="compose-send-btn">
                <Send size={14} className="mr-1" /> {sending ? "Sending…" : "Send"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

const MessageList = ({ items, side, onClick }) => {
  if (items.length === 0) {
    return <div className="p-6 text-center text-sm text-slate-400">No messages.</div>;
  }
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((m) => {
        const peerName = side === "from" ? m.from_name : m.to_name;
        const peerEmail = side === "from" ? m.from_internal_email : m.to_internal_email;
        return (
          <li key={m.id} onClick={() => onClick(m)} className={`p-3 cursor-pointer hover:bg-slate-50 ${!m.read_at && side === "from" ? "bg-blue-50/40" : ""}`} data-testid={`msg-${m.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-slate-900 truncate">{peerName}</p>
              <p className="text-xs text-slate-400 shrink-0">{new Date(m.created_at).toLocaleDateString()}</p>
            </div>
            <p className="text-xs text-slate-500 truncate">{peerEmail}</p>
            <p className="text-sm text-slate-700 truncate mt-0.5">{m.subject}</p>
          </li>
        );
      })}
    </ul>
  );
};

export default MessagesPage;
